import Link from '../../components/framework/link'
import React from 'react'
import { Container } from '../../components/ui/container'
import { FEATURED_LOCATIONS, POPULAR_STATES, stateHref } from '../../config/locations'
import { FEATURED_SERVICES, serviceHref } from '../../config/services'
import { FOOTER_NAV, LEGAL_LINKS, SITE } from '../../config/site'
import { BrandLogo } from './brand-logo'
import { MobileFooterNav } from './mobile-footer-nav'

/** Six each. A directory footer will absorb every link it is offered. */
const FOOTER_SERVICES = FEATURED_SERVICES.slice(0, 6)
const FOOTER_STATES = POPULAR_STATES.slice(0, 6)

/** Inline, dot-separated discovery row — compact on desktop, wraps on phones. */
function DiscoveryRow({
  label,
  links,
}: {
  label: string
  links: readonly { name: string; href: string }[]
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-5">
      <p className="text-eyebrow shrink-0 text-white/45 uppercase sm:w-36">{label}</p>
      <ul className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
        {links.map((link, i) => (
          <li key={link.href} className="flex items-center gap-1.5">
            <Link
              href={link.href}
              className="focus-ring-light text-sm text-white/65 motion-micro hover:text-white"
            >
              {link.name}
            </Link>
            {i < links.length - 1 ? (
              <span className="text-white/20" aria-hidden="true">
                ·
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The global site footer.
 *
 * Rendered from the root layout, so every page gets it. It is a server
 * component apart from the mobile accordions.
 *
 * Note the seam with the section above it: the contractor CTA is a navy panel
 * sitting inside a WHITE section, so there is already a band of white between
 * that panel and this footer. Without it the two navies would run together
 * into one enormous dark block.
 */
export function SiteFooter() {
  const year = new Date().getFullYear()

  return (
    <footer className="bg-navy">
      <Container>
        {/* ── Brand + navigation ─────────────────────────────────────────── */}
        <div className="grid gap-10 pt-16 pb-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.75fr)] lg:gap-16 lg:pt-20 lg:pb-14">
          <div>
            <BrandLogo tone="light" />
            <p className="mt-5 max-w-[23rem] text-sm leading-relaxed text-white/60">
              Helping homeowners discover local professionals, and giving contractors a place to
              keep their business information visible and useful.
            </p>
          </div>

          {/* Desktop columns */}
          <nav aria-label="Footer" className="hidden lg:grid lg:grid-cols-3 lg:gap-8">
            {FOOTER_NAV.map((group) => (
              <div key={group.id}>
                <h3 className="text-[0.8125rem] font-semibold tracking-[0.02em] text-white">
                  {group.heading}
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {group.links.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="focus-ring-light text-[0.9375rem] text-white/60 motion-micro hover:text-white"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {/* Phones get the same groups as accordions */}
          <MobileFooterNav groups={FOOTER_NAV} />
        </div>

        {/* ── Discovery ──────────────────────────────────────────────────── */}
        {/* Plain nouns, not "Popular …". Nothing measures popularity, so the
            word would be describing behaviour no system has observed. */}
        <div className="flex flex-col gap-4 border-t border-white/10 py-8">
          <DiscoveryRow
            label="Services"
            links={FOOTER_SERVICES.map((s) => ({ name: s.name, href: serviceHref(s.slug) }))}
          />
          <DiscoveryRow
            label="States"
            links={FOOTER_STATES.map((s) => ({ name: s.name, href: stateHref(s.slug) }))}
          />
          <DiscoveryRow
            label="Cities"
            links={FEATURED_LOCATIONS.map((l) => ({
              name: `${l.city}, ${l.stateCode}`,
              href: `/locations/${l.stateSlug}/${l.citySlug}`,
            }))}
          />
        </div>

        {/* ── Disclaimer + legal ─────────────────────────────────────────── */}
        <div className="border-t border-white/10 py-7">
          <p className="max-w-3xl text-[0.75rem] leading-relaxed text-white/45">
            Business information on {SITE.name} may be provided by third parties or by business
            owners, and listings are not endorsements. Homeowners should independently evaluate any
            contractor before hiring.
          </p>

          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[0.8125rem] text-white/45">
              © {year} {SITE.name}
            </p>

            <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {LEGAL_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="focus-ring-light text-[0.8125rem] text-white/55 motion-micro hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Container>
    </footer>
  )
}
